import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-semibold transition-all cursor-pointer outline-none focus:outline-none focus-visible:outline-none active:outline-none disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[inset_0_1px_1px_rgba(255,255,255,0.45),0_0_0_4px_rgba(249,115,22,0.12),0_0_0_5px_rgba(255,255,255,0.45)] hover:-translate-y-[0.5px] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.5),0_0_0_4px_rgba(249,115,22,0.18),0_0_0_6px_rgba(255,255,255,0.55)] active:translate-y-[0.5px]",
        primary:
          "bg-[#38a2fe] text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.45),0_0_0_4px_rgba(56,162,254,0.12),0_0_0_5px_rgba(255,255,255,0.45)] hover:-translate-y-[0.5px] hover:brightness-105 hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.5),0_0_0_4px_rgba(56,162,254,0.18),0_0_0_6px_rgba(255,255,255,0.55)] active:translate-y-[0.5px] active:brightness-90",
        secondary:
          "bg-gray-100 text-gray-700 hover:bg-gray-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.7),0_0_0_1px_rgba(148,163,184,0.5),0_2px_6px_rgba(100,116,139,0.18)] hover:-translate-y-[0.5px] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.6),0_4px_10px_rgba(100,116,139,0.25)] active:translate-y-[0.5px] active:brightness-95",
        grey:
          "bg-gray-100 text-gray-700 hover:bg-gray-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.7),0_0_0_1px_rgba(148,163,184,0.5),0_2px_6px_rgba(100,116,139,0.18)] hover:-translate-y-[0.5px] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.8),0_0_0_1px_rgba(148,163,184,0.6),0_4px_10px_rgba(100,116,139,0.25)] active:translate-y-[0.5px] active:brightness-95",
        destructive:
          "bg-red-600 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.4),0_0_0_4px_rgba(220,38,38,0.12),0_0_0_5px_rgba(255,255,255,0.45)] hover:-translate-y-[0.5px] hover:brightness-105 hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.45),0_0_0_4px_rgba(220,38,38,0.18),0_0_0_6px_rgba(255,255,255,0.55)] active:translate-y-[0.5px] active:brightness-90",
        success:
          "bg-green-600 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.4),0_0_0_4px_rgba(22,163,74,0.12),0_0_0_5px_rgba(255,255,255,0.45)] hover:-translate-y-[0.5px] hover:brightness-105 hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.45),0_0_0_4px_rgba(22,163,74,0.18),0_0_0_6px_rgba(255,255,255,0.55)] active:translate-y-[0.5px] active:brightness-90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-gray-100 text-gray-700",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-6 py-2.5",
        sm: "h-8 px-4 text-sm",
        lg: "h-12 px-8 text-lg",
        xl: "h-14 px-8 py-3 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
